/**
 * Demolition Lab — DEV-gated runtime invariant monitor (evaluation framework V1a).
 *
 * Zero overhead in production: every entry point is a no-op when !import.meta.env.DEV.
 * Violations accumulate in a ring buffer (cap 64) exposed via __demolitionState.
 */

export type InvariantCode =
  | 'nan-inf'
  | 'speed-ceiling'
  | 'airborne-sleeper'
  | 'never-sleeps'
  | 'cell-growth'
  | 'dyn-cap'
  | 'bookkeeping'
  | 'volume-ledger'
  | 'max-live-cells';

export interface InvariantViolation {
  code: InvariantCode;
  bodyId?: number;
  value: number;
  simTime: number;
}

export interface InvariantMonitorState {
  violations: InvariantViolation[];
  /** Total pushes (may exceed ring length). */
  count: number;
  /** Live fragment volume after last sample (m³). */
  lastLiveVolume: number;
  /** bodyId → sim-time when |v| first dropped below threshold while awake. */
  lowVelSince: Map<number, number>;
  /** Accumulated DEV ms spent in monitor this session. */
  overheadMs: number;
  /** Number of full-step samples (for avg overhead). */
  sampleCount: number;
}

const RING_CAP = 64;
/** Chunk ceiling: MAX_LAUNCH_SPEED(18) × 1.5 = 27; allow slight slack for one-frame spikes. */
const CHUNK_SPEED_CEILING = 28;
/** Slightly above clamp target (80) so float residue after clamp does not trip. */
const CANNONBALL_SPEED_CEILING = 82;
/** Above this Y a sleep is "airborne"; ground/pile band is never an airborne freeze. */
const AIRBORNE_SLEEP_Y = 2.5;
const NEVER_SLEEP_VEL = 0.05;
const NEVER_SLEEP_SEC = 12;
const VOLUME_TOL = 0.01; // 1%

let state: InvariantMonitorState | null = null;

function isDev(): boolean {
  return import.meta.env.DEV === true;
}

export function createInvariantMonitor(): InvariantMonitorState {
  const s: InvariantMonitorState = {
    violations: [],
    count: 0,
    lastLiveVolume: 0,
    lowVelSince: new Map(),
    overheadMs: 0,
    sampleCount: 0,
  };
  if (isDev()) state = s;
  return s;
}

export function resetInvariantMonitor(mon: InvariantMonitorState | null | undefined) {
  if (!isDev() || !mon) return;
  mon.violations.length = 0;
  mon.count = 0;
  mon.lastLiveVolume = 0;
  mon.lowVelSince.clear();
  // Keep overhead counters across reset for session measurement.
}

export function getInvariantMonitor(): InvariantMonitorState | null {
  return isDev() ? state : null;
}

export function pushInvariant(
  mon: InvariantMonitorState | null | undefined,
  code: InvariantCode,
  value: number,
  simTime: number,
  bodyId?: number,
) {
  if (!isDev() || !mon) return;
  mon.count += 1;
  const entry: InvariantViolation = { code, value, simTime, bodyId };
  if (mon.violations.length >= RING_CAP) mon.violations.shift();
  mon.violations.push(entry);
}

/** Snapshot for bridge: count + copy of ring. */
export function invariantSnapshot(mon: InvariantMonitorState | null | undefined): {
  count: number;
  violations: InvariantViolation[];
  overheadMsPerStep: number;
} {
  if (!isDev() || !mon) {
    return { count: 0, violations: [], overheadMsPerStep: 0 };
  }
  return {
    count: mon.count,
    violations: mon.violations.slice(),
    overheadMsPerStep: mon.sampleCount > 0 ? mon.overheadMs / mon.sampleCount : 0,
  };
}

/**
 * Assert at sleepBody call site: body above the ground band must be supported.
 * Call only when about to sleep; records airborne-sleeper if the body is floating.
 *
 * Displacement alone is not evidence of support — a body frozen mid-air also
 * has zero displacement. A floating freeze is identified by having nothing in
 * contact (contactPoints === 0). When contact telemetry is unavailable
 * (contactPoints undefined), fall back to the legacy displacement / near-ground
 * predicate so a missing capability never invents false positives.
 */
export function assertSleepAllowed(
  mon: InvariantMonitorState | null | undefined,
  opts: {
    bodyId: number;
    y: number;
    speed: number;
    displacementOk: boolean;
    nearGround: boolean;
    simTime: number;
    /** Manifold contact point count; undefined when contact-load cap is missing. */
    contactPoints?: number;
  },
) {
  if (!isDev() || !mon) return;
  // Ground / rubble-pile band (y < 2.5): never an airborne freeze — late settle
  // and residual rollers legitimately sleep here even if |v| is still a few cm/s.
  if (opts.y < AIRBORNE_SLEEP_Y) return;

  // Definite zero-contact reading: airborne freeze, regardless of displacement.
  if (opts.contactPoints === 0) {
    pushInvariant(mon, 'airborne-sleeper', opts.y, opts.simTime, opts.bodyId);
    return;
  }
  // Supported (contactPoints > 0): legitimate rest at any height.
  if (opts.contactPoints !== undefined && opts.contactPoints > 0) return;

  // Capability missing: legacy displacement / near-ground low-energy rule.
  const lowEnergy = opts.speed < NEVER_SLEEP_VEL;
  const ok = opts.displacementOk || (opts.nearGround && lowEnergy);
  if (!ok) {
    pushInvariant(mon, 'airborne-sleeper', opts.y, opts.simTime, opts.bodyId);
  }
}

export interface StepMonitorInput {
  mon: InvariantMonitorState;
  simTime: number;
  /** Flat transforms: 7 floats/body (px,py,pz,qx,qy,qz,qw). */
  transforms: Float32Array;
  bodyIds: Int32Array | number[];
  /** bodyId → kind for speed ceilings. */
  isCannonball: (bodyId: number) => boolean;
  /** Optional: read linear velocity; return null if unavailable. */
  getSpeed: (bodyId: number) => number | null;
  /** Is body still considered awake (for never-sleeps). */
  isAwake: (bodyId: number) => boolean;
  cellCount: number;
  staticCells: number;
  dynamicChunks: number;
  removedChunks: number;
  maxLiveCells: number;
  liveCellCount: number;
  maxDynamicChunks: number;
  fractured: boolean;
  pristineCellCount: number;
  /** Sum of live static+dynamic fragment volumes (m³). */
  liveVolume: number;
  /** If true, a carve just happened — volume may jump by expectedDelta. */
  carveEvent?: { expectedDelta: number } | null;
}

/**
 * Cheap scalar checks on flat buffers. DEV only.
 */
export function runInvariantStep(input: StepMonitorInput) {
  if (!isDev()) return;
  const t0 = performance.now();
  const { mon, simTime } = input;

  // NaN/Inf in transforms
  const t = input.transforms;
  for (let i = 0; i < t.length; i += 1) {
    if (!Number.isFinite(t[i])) {
      const bodyIdx = Math.floor(i / 7);
      const bodyId = input.bodyIds[bodyIdx] as number | undefined;
      pushInvariant(mon, 'nan-inf', t[i], simTime, bodyId);
      break; // one per step is enough signal
    }
  }

  // Speed ceilings + never-sleeps
  for (let i = 0; i < input.bodyIds.length; i += 1) {
    const bodyId = input.bodyIds[i] as number;
    if (bodyId == null) continue;
    const speed = input.getSpeed(bodyId);
    if (speed == null || !Number.isFinite(speed)) continue;
    const ceiling = input.isCannonball(bodyId) ? CANNONBALL_SPEED_CEILING : CHUNK_SPEED_CEILING;
    if (speed > ceiling) {
      pushInvariant(mon, 'speed-ceiling', speed, simTime, bodyId);
    }

    // Never-sleeps: chunk with |v| < 0.05 sustained > 12s and still awake
    if (!input.isCannonball(bodyId) && input.isAwake(bodyId)) {
      if (speed < NEVER_SLEEP_VEL) {
        if (!mon.lowVelSince.has(bodyId)) mon.lowVelSince.set(bodyId, simTime);
        else if (simTime - (mon.lowVelSince.get(bodyId) ?? simTime) > NEVER_SLEEP_SEC) {
          pushInvariant(mon, 'never-sleeps', speed, simTime, bodyId);
          mon.lowVelSince.set(bodyId, simTime); // re-arm to avoid flood
        }
      } else {
        mon.lowVelSince.delete(bodyId);
      }
    } else {
      mon.lowVelSince.delete(bodyId);
    }
  }

  // Cell-growth bound
  if (input.liveCellCount > input.maxLiveCells) {
    pushInvariant(mon, 'cell-growth', input.liveCellCount, simTime);
  }

  // Dyn-cap bound
  if (input.dynamicChunks > input.maxDynamicChunks) {
    pushInvariant(mon, 'dyn-cap', input.dynamicChunks, simTime);
  }

  // Bookkeeping identity
  if (!input.fractured) {
    if (input.cellCount !== input.pristineCellCount) {
      pushInvariant(mon, 'bookkeeping', input.cellCount - input.pristineCellCount, simTime);
    }
  } else {
    const sum = input.staticCells + input.dynamicChunks + input.removedChunks;
    if (sum !== input.cellCount) {
      pushInvariant(mon, 'bookkeeping', sum - input.cellCount, simTime);
    }
  }

  // Volume ledger: live volume only decreases except known carve deltas (±1%)
  if (mon.lastLiveVolume > 0 && input.liveVolume > mon.lastLiveVolume * (1 + VOLUME_TOL)) {
    const expected = input.carveEvent?.expectedDelta ?? 0;
    const allowed = mon.lastLiveVolume + expected;
    if (input.liveVolume > allowed * (1 + VOLUME_TOL) && expected <= 0) {
      pushInvariant(mon, 'volume-ledger', input.liveVolume - mon.lastLiveVolume, simTime);
    }
  }
  mon.lastLiveVolume = input.liveVolume;

  mon.overheadMs += performance.now() - t0;
  mon.sampleCount += 1;
}
